// Sprint 7 — real, server-backed owner Booking management. Lives in the creator
// App Detail (owner side of the owner/customer boundary), NEVER in the customer
// app. Reads every booking for an app the creator OWNS via
// GET /v1/apps/:appId/bookings (creator session + app_data_owner_select RLS), and
// updates status via PATCH /v1/apps/:appId/bookings/:id. No fabricated rows: the
// list is exactly what the server returns. Honest loading / empty / error states.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { CalendarCheck, RotateCw, Check, X, CircleCheck, Clock, Mail, Phone, User, CreditCard, Undo2 } from 'lucide-preact';
import { t } from '../lib/i18n';

// The canonical server-backed booking record shape. The generated Booking runtime
// writes these fields into app_data.data; this panel reads them back. Every field
// is optional-tolerant so an older/leaner record never crashes the panel.
interface Booking {
  id: string;
  serviceName?: string;
  serviceId?: string;
  slot?: string;
  date?: string;
  time?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  status?: string;
  paymentStatus?: string; // §7P manual payment state
  selectedPaymentMethod?: string; // §7C-HR — the option the customer chose at book time
  createdAt?: string;
  updatedAt?: string;
}

// §7C-HR — human label for the customer's chosen payment option. Omitted for
// none/undefined so a non-paid booking shows nothing extra. §S680: SERVER
// values map to i18n KEYS (resolved via t() at render); an unknown raw value
// simply shows nothing extra, as before.
const PAYMENT_METHOD_LABEL: Record<string, string> = {
  external_link: 'mgmt.pm.externalLink',
  pay_at_business: 'mgmt.pm.payAtBusiness',
};

type Load =
  | { s: 'loading' }
  | { s: 'ready'; items: Booking[] }
  | { s: 'error'; code?: number }
  | { s: 'unavailable' }; // 503 — server store not configured

type Status = 'requested' | 'confirmed' | 'cancelled' | 'completed';

// §S680 — labels are i18n KEYS (resolved via t() at render time, never at
// module scope, so a late initLocale() is always honored).
const STATUS_META: Record<Status, { labelKey: string; cls: string }> = {
  requested: { labelKey: 'mgmt.st.requested', cls: 'pf-badge--warn' },
  confirmed: { labelKey: 'mgmt.st.confirmed', cls: 'pf-badge--ok' },
  completed: { labelKey: 'mgmt.st.completed', cls: 'pf-badge--seed' },
  cancelled: { labelKey: 'mgmt.st.cancelled', cls: 'pf-badge--err' }
};

// Which status transitions an owner can make from a given state.
const NEXT_ACTIONS: Record<string, { to: Status; labelKey: string; Icon: typeof Check }[]> = {
  requested: [{ to: 'confirmed', labelKey: 'mgmt.act.confirm', Icon: Check }, { to: 'cancelled', labelKey: 'mgmt.act.decline', Icon: X }],
  confirmed: [{ to: 'completed', labelKey: 'mgmt.act.markDone', Icon: CircleCheck }, { to: 'cancelled', labelKey: 'mgmt.act.cancel', Icon: X }],
  completed: [],
  cancelled: []
};

// §7P / §7C-HR — MANUAL payment states (no auto-verified state). The owner moves
// these by hand after checking their external payment provider. external_pending +
// pay_at_business_pending are customer-stamped at book time (the option they chose);
// the owner treats them like awaiting_payment.
type PayStatus = 'not_required' | 'awaiting_payment' | 'external_pending' | 'pay_at_business_pending' | 'marked_paid' | 'payment_canceled' | 'refunded_manual';
const PAY_META: Record<PayStatus, { labelKey: string; cls: string }> = {
  not_required: { labelKey: 'mgmt.pay.notRequired', cls: '' },
  awaiting_payment: { labelKey: 'mgmt.pay.awaiting', cls: 'pf-badge--warn' },
  external_pending: { labelKey: 'mgmt.pay.awaitingLink', cls: 'pf-badge--warn' },
  pay_at_business_pending: { labelKey: 'mgmt.pay.payAtBusiness', cls: 'pf-badge--warn' },
  marked_paid: { labelKey: 'mgmt.pay.paid', cls: 'pf-badge--ok' },
  payment_canceled: { labelKey: 'mgmt.pay.canceled', cls: 'pf-badge--err' },
  refunded_manual: { labelKey: 'mgmt.pay.refunded', cls: 'pf-badge--seed' }
};
// Manual transitions the owner can make from each payment state. The two pending
// states behave like awaiting_payment (→ marked_paid / payment_canceled).
const PAY_NEXT: Record<string, { to: PayStatus; labelKey: string; Icon: typeof Check }[]> = {
  awaiting_payment: [{ to: 'marked_paid', labelKey: 'mgmt.act.markPaid', Icon: CreditCard }, { to: 'payment_canceled', labelKey: 'mgmt.act.cancelPayment', Icon: X }],
  external_pending: [{ to: 'marked_paid', labelKey: 'mgmt.act.markPaid', Icon: CreditCard }, { to: 'payment_canceled', labelKey: 'mgmt.act.cancelPayment', Icon: X }],
  pay_at_business_pending: [{ to: 'marked_paid', labelKey: 'mgmt.act.markPaid', Icon: CreditCard }, { to: 'payment_canceled', labelKey: 'mgmt.act.cancelPayment', Icon: X }],
  marked_paid: [{ to: 'refunded_manual', labelKey: 'mgmt.act.refunded', Icon: Undo2 }, { to: 'awaiting_payment', labelKey: 'mgmt.act.markUnpaid', Icon: Undo2 }],
  payment_canceled: [{ to: 'awaiting_payment', labelKey: 'mgmt.act.reopen', Icon: RotateCw }],
  refunded_manual: [],
  not_required: []
};

function whenLabel(b: Booking): string {
  if (b.slot) return b.slot;
  if (b.date || b.time) return [b.date, b.time].filter(Boolean).join(' · ');
  return '—';
}

export function ManageBookings({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ s: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState('');

  const fetchBookings = useCallback(() => {
    setLoad({ s: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/bookings`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setLoad({ s: 'unavailable' });
        if (!r.ok) return setLoad({ s: 'error', code: r.status });
        const data = (await r.json()) as { items?: Booking[] };
        setLoad({ s: 'ready', items: Array.isArray(data.items) ? data.items : [] });
      })
      .catch(() => setLoad({ s: 'error' }));
  }, [appId]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  async function patchBooking(b: Booking, payload: { status?: Status; paymentStatus?: PayStatus }): Promise<void> {
    if (busyId) return;
    setBusyId(b.id);
    setActionErr('');
    try {
      const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/bookings/${encodeURIComponent(b.id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (r.ok) {
        const data = (await r.json()) as { item?: Booking };
        const updated = data.item;
        setLoad((cur) => (cur.s === 'ready'
          ? { s: 'ready', items: cur.items.map((x) => (x.id === b.id ? { ...x, ...(updated ?? payload) } : x)) }
          : cur));
      } else if (r.status === 409) {
        // owner-update RLS policy (migration 009) not applied yet — honest, no fake success
        setActionErr(t('mgmt.bk.err409'));
      } else if (r.status === 404) {
        setActionErr(t('mgmt.bk.err404'));
      } else {
        setActionErr(t('mgmt.bk.errUpdate'));
      }
    } catch {
      setActionErr(t('mgmt.errConn'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section aria-label={t('mgmt.bk.title')} data-mgmt-panel="bookings" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <CalendarCheck size={15} /> {t('mgmt.bk.title')}
          {load.s === 'ready' && <span className="pf-badge" style={{ marginInlineStart: 6 }}>{load.items.length}</span>}
        </span>
        <button type="button" className="pf-btn pf-btn--sm" onClick={fetchBookings} disabled={load.s === 'loading'}>
          <RotateCw size={13} /> {t('mgmt.refresh')}
        </button>
      </div>

      {actionErr && <p style={{ fontSize: 12.5, color: 'var(--color-danger)', margin: 0 }}>{actionErr}</p>}

      {load.s === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden="true">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-shimmer" style={{ height: 72, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {load.s === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.bk.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.bk.unavailableS')}</span></span></div>
      )}

      {load.s === 'error' && (
        <div className="pf-mgmt-panel">
          <span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.bk.errT')}</span><span className="pf-mgmt-panel__s">{load.code === 401 ? t('mgmt.bk.errAuth') : t('mgmt.bk.errGeneric')}</span></span>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchBookings}><RotateCw size={13} /> {t('common.retry')}</button>
        </div>
      )}

      {load.s === 'ready' && load.items.length === 0 && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><CalendarCheck size={22} /></span>
          <p className="pf-empty__title">{t('mgmt.bk.emptyT')}</p>
          <p className="pf-empty__lede">{t('mgmt.bk.emptyS')}</p>
        </div>
      )}

      {load.s === 'ready' && load.items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {load.items.map((b) => {
            const status = (b.status && b.status in STATUS_META ? b.status : 'requested') as Status;
            const meta = STATUS_META[status];
            const actions = NEXT_ACTIONS[status] ?? [];
            // §7P — payment is only surfaced when a booking actually carries a
            // (non-"not_required") payment state, so non-paid apps look unchanged.
            const pay = (b.paymentStatus && b.paymentStatus in PAY_META ? b.paymentStatus : '') as PayStatus | '';
            const payMeta = pay && pay !== 'not_required' ? PAY_META[pay] : null;
            const payActions = pay ? (PAY_NEXT[pay] ?? []) : [];
            // §7C-HR — the option the customer chose at book time (display only).
            const methodLabelKey = b.selectedPaymentMethod ? PAYMENT_METHOD_LABEL[b.selectedPaymentMethod] : undefined;
            return (
              <div key={b.id} style={cardStyle}>
                <div className="pf-row" style={{ alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{b.serviceName || b.serviceId || t('mgmt.bk.appointment')}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Clock size={12} /> {whenLabel(b)}
                    </span>
                  </div>
                  <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span className={`pf-badge ${meta.cls}`}>{t(meta.labelKey)}</span>
                    {payMeta && <span className={`pf-badge ${payMeta.cls}`}><CreditCard size={11} /> {t(payMeta.labelKey)}</span>}
                    {methodLabelKey && <span className="pf-badge">{t(methodLabelKey)}</span>}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: 'var(--fg-muted)' }}>
                  {b.customerName && <span style={chip}><User size={12} /> {b.customerName}</span>}
                  {b.customerEmail && <span style={chip}><Mail size={12} /> {b.customerEmail}</span>}
                  {b.customerPhone && <span style={chip}><Phone size={12} /> {b.customerPhone}</span>}
                </div>
                {b.notes && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--fg)', whiteSpace: 'pre-wrap' }}>{b.notes}</p>}

                {actions.length > 0 && (
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 2 }}>
                    {actions.map((a) => (
                      <button key={a.to} type="button"
                        className={`pf-btn pf-btn--sm${a.to === 'cancelled' ? ' pf-btn--danger' : a.to === 'confirmed' || a.to === 'completed' ? ' pf-btn--soft' : ''}`}
                        disabled={busyId === b.id} onClick={() => void patchBooking(b, { status: a.to })}>
                        <a.Icon size={13} /> {busyId === b.id ? '…' : t(a.labelKey)}
                      </button>
                    ))}
                  </div>
                )}

                {payActions.length > 0 && (
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{t('mgmt.bk.paymentRow')}</span>
                    {payActions.map((a) => (
                      <button key={a.to} type="button"
                        className={`pf-btn pf-btn--sm${a.to === 'marked_paid' ? ' pf-btn--soft' : a.to === 'payment_canceled' ? ' pf-btn--danger' : ''}`}
                        disabled={busyId === b.id} onClick={() => void patchBooking(b, { paymentStatus: a.to })}>
                        <a.Icon size={13} /> {busyId === b.id ? '…' : t(a.labelKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const cardStyle: JSX.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, padding: 14,
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)'
};
const chip: JSX.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5 };
